import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Box,
  Slide,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close,
  Save,
  ReceiptLong,
  AttachMoney,
  FactCheck,
} from "@mui/icons-material";
import { useAuth } from "../../context/context";
import priceDetailsService from "../../services/priceDetailsService";
import PriceDetailsForm from "./PriceDetailsForm";
import LotDetailsSections from "./LotDetailsSections";
import placeholderImage from "../../assets/image-placeholder.webp";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Estado inicial por defecto (modo creación)
const getDefaultData = () => ({
  active: true,
  iva: true,
  hasLicensePlate: true,
  ownerPrice: "",
  taxAppraisal: "",
  commissionPercentage: "",
  operationalCosts: "",
  transferFee: "",
  additionalCosts: [],
});

// Genera ids para los costos adicionales cargados (necesarios como keys del form)
let loadCostId = 0;
const withCostIds = (costs = []) =>
  costs.map((c) => {
    loadCostId += 1;
    return {
      id: c.id || `loaded_${Date.now()}_${loadCostId}`,
      name: c.name ?? "",
      value: c.value ?? "",
      description: c.description ?? "",
    };
  });

/**
 * Mapea la respuesta del backend (modelo DirectSalePriceDetails) a la forma
 * de inputs que usa el formulario.
 */
const mapBackendToForm = (pd) => ({
  active: pd.active !== false,
  iva: pd.iva !== false,
  hasLicensePlate: pd.hasLicensePlate !== false,
  ownerPrice: pd.ownerPrice ?? "",
  taxAppraisal: pd.taxAppraisal ?? "",
  commissionPercentage: pd.commissionPercentage ?? "",
  operationalCosts: pd.operationalCosts ?? "",
  transferFee: pd.transferFee ?? "",
  additionalCosts: withCostIds(pd.additionalCosts),
});

// Construye el payload de inputs de precio (sin ids internos) para el backend.
const buildPricePayload = (data) => ({
  active: data.active !== false,
  iva: data.iva,
  hasLicensePlate: data.hasLicensePlate,
  ownerPrice: parseFloat(data.ownerPrice) || 0,
  taxAppraisal: parseFloat(data.taxAppraisal) || 0,
  commissionPercentage: parseFloat(data.commissionPercentage) || 0,
  operationalCosts: parseFloat(data.operationalCosts) || 0,
  transferFee: parseFloat(data.transferFee) || 0,
  additionalCosts: (data.additionalCosts || [])
    .filter((c) => c.name)
    .map((c) => ({
      name: c.name,
      value: parseFloat(c.value) || 0,
      description: c.description || null,
    })),
});

// Clave estable del estado de precio para detectar cambios sin guardar.
const priceKey = (data) => JSON.stringify(buildPricePayload(data));

/**
 * Modal full-screen para crear/editar los Detalles de Precio de venta directa
 * de un producto. Solo persiste inputs; el backend recalcula los derivados y
 * aplica el flujo 4-eyes (al editar un registro existente puede quedar pendiente
 * de aprobación de un segundo usuario).
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Object} props.product - Producto del catálogo (product.id === lotId)
 * @param {() => void} [props.onSaved] - Callback tras guardar con éxito
 */
const PriceDetailsModal = ({ open, onClose, product, onSaved }) => {
  const { token } = useAuth();
  const lotId = product?.id;

  const [data, setData] = useState(getDefaultData());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [sectionsDirty, setSectionsDirty] = useState(false);
  const [initialPriceKey, setInitialPriceKey] = useState(() =>
    priceKey(getDefaultData()),
  );
  const sectionsRef = useRef(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Cargar detalles existentes al abrir
  useEffect(() => {
    if (!open || !lotId) return;

    let active = true;
    setError(null);
    setData(getDefaultData());
    setActiveTab(0);
    setSectionsDirty(false);
    setInitialPriceKey(priceKey(getDefaultData()));
    setLoading(true);

    priceDetailsService
      .getPriceDetails(lotId, token)
      .then((pd) => {
        if (!active) return;
        if (pd) {
          const mapped = mapBackendToForm(pd);
          setData(mapped);
          setInitialPriceKey(priceKey(mapped));
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(err.message || "Error al cargar los detalles de precio");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, lotId, token]);

  const handleChange = useCallback((patch) => {
    setData((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  // ¿Cambió el detalle de precio respecto a lo cargado?
  const priceDirty = priceKey(data) !== initialPriceKey;
  const isDirty = priceDirty || sectionsDirty;

  // Guardado GLOBAL: persiste lo que haya cambiado en AMBAS pestañas
  // (detalle de precio y secciones de características/documentación),
  // sin importar desde qué botón se dispare.
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);

    let priceSaved = false;
    let pendingInfo = false;
    let sectionsSaved = false;
    let hadError = false;

    // 1) Detalle de precio (solo si cambió)
    if (priceDirty) {
      try {
        const result = await priceDetailsService.upsertPriceDetails(
          lotId,
          buildPricePayload(data),
          token,
        );
        priceSaved = true;
        if (result?.data?.pendingChange) pendingInfo = true;
        // Actualizar snapshot para no volver a guardar lo mismo
        setInitialPriceKey(priceKey(data));
      } catch (err) {
        hadError = true;
        setError(err.message || "Error al guardar los detalles de precio");
      }
    }

    // 2) Secciones de cotización (solo si cambió y no falló el paso anterior)
    if (!hadError && sectionsRef.current) {
      const secRes = await sectionsRef.current.save();
      if (secRes?.error) {
        hadError = true;
        setSnackbar({
          open: true,
          message: `❌ ${secRes.error}`,
          severity: "error",
        });
      } else if (secRes?.saved) {
        sectionsSaved = true;
      }
    }

    if (hadError) {
      setSaving(false);
      return;
    }

    if (priceSaved || sectionsSaved) {
      const parts = [];
      if (priceSaved) {
        parts.push(
          pendingInfo
            ? "Detalle de precio enviado a revisión (4-ojos)"
            : "Detalle de precio guardado",
        );
      }
      if (sectionsSaved) {
        parts.push("Características/Documentación actualizadas");
      }
      setSnackbar({
        open: true,
        message: parts.join(" · "),
        severity: pendingInfo ? "info" : "success",
      });

      if (onSaved) onSaved();

      // Cerrar tras un breve margen para que se vea el snackbar
      setTimeout(() => {
        onClose();
      }, 1200);
    } else {
      setSnackbar({
        open: true,
        message: "No hay cambios para guardar",
        severity: "info",
      });
    }

    setSaving(false);
  }, [priceDirty, data, lotId, token, onSaved, onClose]);

  // Datos del producto para el header
  const productImage =
    (product?.images && product.images.length > 0 && product.images[0]) ||
    product?.image ||
    placeholderImage;
  const productPrice =
    product?.formattedPrice ||
    (product?.price != null
      ? `$${new Intl.NumberFormat("es-CL").format(product.price)}`
      : "Sin precio");
  const productSubtitle = [
    product?.category,
    product?.marca || product?.brand,
    product?.model,
  ]
    .filter(Boolean)
    .join(" · ");
  // IVA: mientras se cargan los detalles usamos el flag del propio producto si existe
  const ivaLabel = data.iva !== false ? "Afecto a IVA" : "Exento de IVA";

  // 🛡️ Evita el click-through: el Dialog vive en un portal, pero los eventos de
  // React burbujean por el árbol de componentes hasta el onClick de la tarjeta.
  const stopPropagation = useCallback((e) => e.stopPropagation(), []);

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={handleClose}
        onClick={stopPropagation}
        TransitionComponent={Transition}
        PaperProps={{ sx: { overflowX: "hidden" } }}
      >
        <AppBar sx={{ position: "relative" }} color="default" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="cerrar"
              disabled={saving}
            >
              <Close />
            </IconButton>
            <Box sx={{ ml: 1, flex: 1, overflow: "hidden" }}>
              <Typography variant="h6" component="div" noWrap>
                Detalle de Precio
              </Typography>
              {product?.name && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                >
                  {product.name}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Save />
                )
              }
              onClick={handleSave}
              disabled={saving || loading || !isDirty}
            >
              {saving ? "Guardando..." : "Guardar todo"}
            </Button>
          </Toolbar>
        </AppBar>

        {/* 🆕 Header con el detalle del producto que se está editando */}
        {product && (
          <Box
            sx={{
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "#fafafa",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                p: { xs: 1.5, sm: 2 },
                maxWidth: 1100,
                mx: "auto",
                width: "100%",
              }}
            >
              <Box
                component="img"
                src={productImage}
                alt={product.name || "Producto"}
                onError={(e) => {
                  e.currentTarget.src = placeholderImage;
                }}
                sx={{
                  width: { xs: "100%", sm: 120 },
                  height: { xs: 160, sm: 90 },
                  objectFit: "cover",
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              />

              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      flex: "1 1 0",
                      minWidth: 0,
                    }}
                  >
                    {product.name || "Producto sin nombre"}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`ID ${product.id}`}
                    sx={{ flexShrink: 0 }}
                  />
                </Box>

                {productSubtitle && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.25,
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {productSubtitle}
                  </Typography>
                )}

                {product.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      mt: 0.5,
                    }}
                  >
                    {product.description}
                  </Typography>
                )}
              </Box>

              <Box
                sx={{
                  textAlign: { xs: "left", sm: "right" },
                  flexShrink: 0,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Precio publicado
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", color: "#004b93", lineHeight: 1.2 }}
                >
                  {productPrice}
                </Typography>
                <Chip
                  icon={<ReceiptLong sx={{ fontSize: 16 }} />}
                  size="small"
                  label={ivaLabel}
                  color={data.iva !== false ? "primary" : "default"}
                  variant={data.iva !== false ? "filled" : "outlined"}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "#fff",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_e, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ maxWidth: 1100, mx: "auto", px: { xs: 1, sm: 2 } }}
          >
            <Tab
              icon={<AttachMoney />}
              iconPosition="start"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  Detalle de Precio
                  {priceDirty && (
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ minHeight: 48, textTransform: "none" }}
            />
            <Tab
              icon={<FactCheck />}
              iconPosition="start"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  Características / Documentación PDF
                  {sectionsDirty && (
                    <Box
                      component="span"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                      }}
                    />
                  )}
                </Box>
              }
              sx={{ minHeight: 48, textTransform: "none" }}
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, sm: 3 },
            maxWidth: 1100,
            mx: "auto",
            width: "100%",
          }}
        >
          <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
            {error && (
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
              >
                {error}
              </Alert>
            )}

            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 300,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <PriceDetailsForm data={data} onChange={handleChange} />
            )}

            {!loading && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={
                    saving ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  onClick={handleSave}
                  disabled={saving || loading || !isDirty}
                >
                  {saving ? "Guardando..." : "Guardar todo"}
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
            <LotDetailsSections
              ref={sectionsRef}
              open={open}
              product={product}
              onDirtyChange={setSectionsDirty}
            />
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        onClick={stopPropagation}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PriceDetailsModal;
